import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ShieldCheck, Copy } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Enrôlement MFA forcé au login (l'entreprise exige la double authentification).
// Reçoit le jeton MFA limité via location.state.
export default function MfaEnroll() {
  const location = useLocation();
  const navigate = useNavigate();
  const { completeLogin } = useAuth();
  const mfaToken = location.state?.mfaToken;

  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [loading, setLoading] = useState(false);
  const requested = useRef(false);

  useEffect(() => {
    if (!mfaToken || requested.current) return;
    requested.current = true;
    axios.post(`${API_URL}/auth/mfa/setup`, {}, {
      headers: { Authorization: `Bearer ${mfaToken}` },
    })
      .then(({ data }) => setSetup(data))
      .catch((err) => {
        toast.error(err.response?.data?.error || 'Session expirée, reconnectez-vous.');
        navigate('/login');
      });
  }, [mfaToken, navigate]);

  if (!mfaToken) {
    return (
      <div className="auth-page">
        <div className="auth-card card" style={{ textAlign: 'center' }}>
          <p>Session introuvable.</p>
          <Link to="/login" className="btn btn-primary" style={{ display: 'inline-block', marginTop: 12 }}>
            Retour à la connexion
          </Link>
        </div>
      </div>
    );
  }

  const handleEnable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/mfa/enable`, { code: code.trim() }, {
        headers: { Authorization: `Bearer ${mfaToken}` },
      });
      setRecoveryCodes(data.recoveryCodes);
      // L'enrôlement forcé débouche directement sur une session complète.
      if (data.token) completeLogin(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Code incorrect');
    } finally {
      setLoading(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard?.writeText(recoveryCodes.join('\n'))
      .then(() => toast.success('Codes copiés'))
      .catch(() => {});
  };

  if (recoveryCodes) {
    return (
      <div className="auth-page">
        <div className="auth-card card">
          <div style={{ textAlign: 'center', marginBottom: 18 }}>
            <ShieldCheck size={40} color="#16a34a" style={{ margin: '0 auto 10px' }} />
            <h2 style={{ margin: 0 }}>Double authentification activée</h2>
          </div>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-light)', lineHeight: 1.6 }}>
            Conservez précieusement ces <strong>codes de récupération</strong> (à usage unique).
            Ils vous permettront de vous connecter si vous perdez votre téléphone.
            <strong> Ils ne seront plus jamais affichés.</strong>
          </p>
          <div style={{
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
            padding: 14, margin: '14px 0', fontFamily: 'monospace', fontSize: '0.95rem',
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, textAlign: 'center',
          }}>
            {recoveryCodes.map(c => <span key={c}>{c}</span>)}
          </div>
          <button className="btn btn-sm" onClick={copyCodes} style={{ marginBottom: 12 }}>
            <Copy size={13} /> Copier les codes
          </button>
          <button className="btn btn-primary" style={{ width: '100%' }}
            onClick={() => navigate('/dashboard')}>
            Continuer vers mon espace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card card">
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <ShieldCheck size={40} color="#2563eb" style={{ margin: '0 auto 10px' }} />
          <h2 style={{ margin: 0 }}>Activez la double authentification</h2>
          <p className="auth-subtitle">Votre entreprise exige cette protection supplémentaire.</p>
        </div>

        {!setup ? (
          <p style={{ textAlign: 'center', color: 'var(--text-light)' }}>Chargement...</p>
        ) : (
          <>
            <ol style={{ fontSize: '0.9rem', color: 'var(--text-light)', lineHeight: 1.7, paddingLeft: 18, marginBottom: 12 }}>
              <li>Installez une application d'authentification (Google Authenticator, Authy...)</li>
              <li>Scannez ce QR code</li>
              <li>Saisissez le code à 6 chiffres affiché</li>
            </ol>
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <img src={setup.qrCode} alt="QR code MFA" style={{ width: 190, height: 190 }} />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-light)', wordBreak: 'break-all' }}>
                Saisie manuelle : <code>{setup.secret}</code>
              </p>
            </div>
            <form onSubmit={handleEnable}>
              <div className="form-group">
                <label>Code à 6 chiffres</label>
                <input value={code} onChange={e => setCode(e.target.value)} required
                  inputMode="numeric" pattern="[0-9]{6}" placeholder="123456"
                  autoComplete="one-time-code"
                  style={{ textAlign: 'center', letterSpacing: 4, fontSize: '1.2rem' }} />
              </div>
              <button className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Activation...' : 'Activer'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
